import {
    fetchGetCreditCardOnboardingPaymentPreference,
    fetchGetPaymentMethod,
    fetchPostRepaymentPaymentMethod,
    fetchPutCreditCardOnboardingPaymentPreference,
    GetCreditCardOnboardingPaymentPreferenceResponse,
    GetPaymentMethodResponse
} from '@iwoca/lapi-client/edge';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { isValidCardType } from '../../../../api/lendingAPI/PaymentAPIHelper';
import * as IwDataDogLogger from '../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { useGetCreditCardOffer } from '../../hooks/useGetCreditCardOffer';

type PaymentPreferenceType = 'full_amount' | 'minimum_amount' | 'fixed_amount';

type PaymentMethodData = {
    cardBrand?: string;
    lastDigits?: string;
    expiryMonth?: number;
    expiryYear?: number;
    paymentMethodId?: string;
    hasExistingCard: boolean;
    payment_preference: GetCreditCardOnboardingPaymentPreferenceResponse['data']['payment_preference'];
    payment_amount: GetCreditCardOnboardingPaymentPreferenceResponse['data']['payment_amount'];
    submitPaymentPreference: (params: {
        paymentPreference: PaymentPreferenceType;
        paymentAmount?: number;
    }) => Promise<unknown>;
    registerDebitCard: (params: {
        stripe: Stripe;
        cardNumberElement: StripeCardNumberElement;
        billingPostcode: string;
        cardholderName: string;
    }) => Promise<void>;
};

const validateCreditCardOfferId = (
    creditCardOfferId: string | undefined,
    reporter: string
): void => {
    if (!creditCardOfferId) {
        IwDataDogLogger.logError('Credit card offer not found', {
            tags: {
                reporter
            }
        });

        throw new Error('Oops, something went wrong.');
    }
};

export const useGetPaymentCardData = (): PaymentMethodData => {
    const stateKey = useStateKey();
    const {
        creditCardOffer,
        getCreditCardOfferLoading,
        getCreditCardOfferError
    } = useGetCreditCardOffer();
    const queryClient = useQueryClient();

    if (!getCreditCardOfferLoading && !getCreditCardOfferError) {
        validateCreditCardOfferId(creditCardOffer?.id, 'useGetPaymentData');
    }

    const { data: cardPaymentMethod } = useQuery({
        queryKey: ['payment_method', stateKey],
        queryFn: () => fetchGetPaymentMethod({ stateKey }),
        select: (data: GetPaymentMethodResponse) =>
            // Find the first card payment method
            data.data?.find((method) => method.payment_method_type === 'card'),
        suspense: true
    });

    const { data: paymentPreference } = useQuery({
        queryKey: ['payment_preference', creditCardOffer?.id],
        queryFn: () =>
            fetchGetCreditCardOnboardingPaymentPreference({
                offerId: creditCardOffer?.id || ''
            }),
        select: (data: GetCreditCardOnboardingPaymentPreferenceResponse) =>
            data?.data,
        suspense: true,
        enabled: Boolean(creditCardOffer) && Boolean(creditCardOffer?.id)
    });

    const { mutateAsync: submitPaymentPreference } = useMutation({
        mutationFn: async ({
            paymentPreference,
            paymentAmount
        }: {
            paymentPreference: PaymentPreferenceType;
            paymentAmount?: number;
        }) => {
            validateCreditCardOfferId(
                creditCardOffer?.id,
                'submitPaymentPreference'
            );
            const response =
                await fetchPutCreditCardOnboardingPaymentPreference({
                    offerId: creditCardOffer!.id!,
                    body: {
                        data: {
                            payment_preference: paymentPreference,
                            payment_amount: paymentAmount
                        }
                    }
                });

            // eslint-disable-next-line compat/compat
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['payment_preference', creditCardOffer?.id]
                }),
                queryClient.invalidateQueries({
                    queryKey: ['credit-card-onboarding-requirements', stateKey]
                }),
                queryClient.invalidateQueries({
                    queryKey: ['customer_state', stateKey]
                })
            ]);

            return response;
        }
    });

    const { mutateAsync: registerDebitCard } = useMutation({
        mutationFn: async ({
            stripe,
            cardNumberElement,
            billingPostcode,
            cardholderName
        }: {
            stripe: Stripe;
            cardNumberElement: StripeCardNumberElement;
            billingPostcode: string;
            cardholderName: string;
        }) => {
            // Create setup intent
            const { data } = await fetchPostRepaymentPaymentMethod({
                stateKey,
                body: {
                    data: {
                        payment_method_type: 'card',
                        role: 'primary'
                    }
                }
            });

            // Create payment method with Stripe
            const { error: paymentMethodError, paymentMethod } =
                await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardNumberElement,
                    billing_details: {
                        name: cardholderName,
                        address: { postal_code: billingPostcode }
                    }
                });

            if (paymentMethodError) {
                throw new Error(
                    paymentMethodError.message || 'Authentication failed'
                );
            }

            if (!paymentMethod) {
                throw new Error('Failed to create payment method');
            }

            // Validate card type
            // @ts-expect-error - Stripe PaymentMethod type doesn't match our internal type but has compatible structure
            if (!isValidCardType(paymentMethod, data.accepted_card_types)) {
                throw new Error('Card type not accepted');
            }

            // Confirm setup intent
            const { error, setupIntent } = await stripe.confirmCardSetup(
                data.secret,
                {
                    payment_method: {
                        card: cardNumberElement,
                        billing_details: {
                            name: cardholderName,
                            address: { postal_code: billingPostcode }
                        }
                    }
                }
            );

            if (error) {
                throw new Error(error.message || 'Authentication failed');
            }

            if (!setupIntent) {
                throw new Error('Setup intent not found');
            }

            // Handle 3DS authentication
            if (setupIntent.status === 'requires_action') {
                const nextActionUrl =
                    setupIntent.next_action?.redirect_to_url?.url;

                if (!nextActionUrl) {
                    throw new Error('3DS authentication URL not found');
                }

                // For 3DS, we need to redirect or handle in an iframe
                // This is simplified - you may need to implement iframe handling
                window.location.href = nextActionUrl;
                return;
            }

            if (setupIntent.status !== 'succeeded') {
                throw new Error('Setup intent failed');
            }

            // eslint-disable-next-line compat/compat
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['payment_method', stateKey]
                }),
                queryClient.invalidateQueries({
                    queryKey: ['credit-card-onboarding-requirements', stateKey]
                }),
                queryClient.invalidateQueries({
                    queryKey: ['customer_state', stateKey]
                })
            ]);
        }
    });

    return {
        cardBrand: cardPaymentMethod?.brand,
        lastDigits: cardPaymentMethod?.last_digits,
        expiryMonth: cardPaymentMethod?.expires?.month,
        expiryYear: cardPaymentMethod?.expires?.year,
        paymentMethodId: cardPaymentMethod?.payment_method_id,
        hasExistingCard: Boolean(cardPaymentMethod),
        //@ts-expect-error:ignore
        payment_preference: paymentPreference?.payment_preference,
        payment_amount: paymentPreference?.payment_amount,
        submitPaymentPreference,
        registerDebitCard
    };
};
